import { useEffect, useMemo, useState } from 'react';

import { WEEK_DAYS_LONG, WEEK_DAYS_SHORT } from '@bitstopco/bitstop-theme/constants';
import { getConsecutive } from '@bitstopco/bitstop-theme/helpers';

import { Box, FormControlLabel, Stack, Typography } from '@mui/material';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import numeral from 'numeral';

import CustomCheckbox from '@/components/ui/CustomCheckbox';

import { LOCATION_VALID_HOURS } from '@/constants/app/location';

import EditLocationScheduleChip from './EditLocationScheduleChip';
import EditLocationScheduleRange from './EditLocationScheduleRange';

export const DEFAULT_WEEK_VALUES: boolean[] = new Array(WEEK_DAYS_SHORT.length).fill(false);
export const DEFAULT_RANGE_VALUES: string[][] = Array.from({ length: WEEK_DAYS_SHORT.length }, () =>
  Array(2).fill(''),
);

/**
 * Index of a short weekday name within WEEK_DAYS_SHORT. Exported so tests can address a day by
 * name: WEEK_DAYS_SHORT is Sunday-first, and the `tests` workspace can't import
 * @bitstopco/bitstop-theme itself (it isn't an npm workspace, so the package isn't in its
 * node_modules), so hardcoded indices there silently drift.
 */
export const dayIndex = (day: string) => WEEK_DAYS_SHORT.indexOf(day);

const getDaysInRange = (dayRange: string) => {
  const [startDay, endDay] = dayRange.split('-');

  // Find the indices of the start and end days in WEEK_DAYS_SHORT array
  const startIndex = WEEK_DAYS_SHORT.findIndex((day) => day === startDay);
  let endIndex = WEEK_DAYS_SHORT.findIndex((day) => day === endDay);

  if (startIndex === -1 || endIndex === -1) {
    // Handle invalid day range
    throw new Error('Invalid day range');
  }

  // If the end day comes before the start day, adjust the end index to consider the full week
  if (endIndex < startIndex) {
    endIndex += WEEK_DAYS_SHORT.length;
  }

  // Create an array containing all the days between start and end days
  const daysInRange: string[] = [];
  for (let i = startIndex; i <= endIndex; i++) {
    const indexInWeek = i % WEEK_DAYS_SHORT.length;
    daysInRange.push(WEEK_DAYS_SHORT[indexInWeek]);
  }

  return daysInRange;
};

export const getValidSchema = (data: string[] = []) => {
  const daysChecked: boolean[] = [...DEFAULT_WEEK_VALUES];
  const rangeValues: string[][] = cloneDeep(DEFAULT_RANGE_VALUES);

  // No schedule stored at all - a location whose Atlas OpeningHours is still `{"Periods": []}`
  // comes back from Athena as an empty array. This has to stay distinguishable from a real
  // '24/7': rendering it as 24/7 made unset hours look deliberately set, and saving the modal
  // then wrote a 24/7 schedule nobody picked. `hoursUnset` keeps the form pristine (emitting
  // nothing) until the user actually chooses something.
  if (!data?.length) {
    return { everydayChecked: false, hoursUnset: true, daysChecked, rangeValues };
  }

  if (data[0] === '24/7') {
    return { everydayChecked: true, hoursUnset: false, daysChecked, rangeValues };
  }

  data.forEach((item) => {
    try {
      const values = item.split(': ');
      if (values[1].toLowerCase() !== 'closed') {
        let days = [values[0]];
        if (values[0].indexOf('-') !== -1) {
          // multiple days
          days = getDaysInRange(values[0]);
        }

        days.forEach((day) => {
          const index = dayIndex(day);
          daysChecked[index] = true;

          const hours = values[1].split(' - ');

          hours.forEach((h, i) => {
            const is24 = hours[0].toLowerCase().indexOf('24-hour') !== -1;

            if (is24) {
              rangeValues[index][i] = LOCATION_VALID_HOURS[0];
            } else {
              const hourValues = h.split(' ');
              const hourSplit = hourValues[0].split(':');
              const value = numeral(+hourSplit[0]).format('00');
              rangeValues[index][i] = `${value}:${hourSplit[1]} ${hourValues[1]}`;
            }
          });
        });
      }
    } catch {
      // something went wrong trying to set a valid rangeValues
    }
  });

  return { everydayChecked: false, hoursUnset: false, daysChecked, rangeValues };
};

export const getValidSchedule = (
  daysChecked: boolean[],
  everydayChecked: boolean,
  rangeValues: string[][],
) => {
  if (everydayChecked) {
    return ['24/7'];
  }

  if (daysChecked.every((v) => !v)) {
    return ['Mon-Sun: Closed'];
  }

  const items: string[] = [];
  const baseConsecutive: number[] = [];

  rangeValues.forEach((values, index) => {
    const opens = daysChecked[index] ? values?.[0] : null;
    const closes = daysChecked[index] ? values?.[1] : null;

    if (opens === '24-Hours') {
      items.push(`${WEEK_DAYS_SHORT[index]}: 24-hour`);
    } else if (!isEmpty(opens) && !isEmpty(closes)) {
      items.push(`${WEEK_DAYS_SHORT[index]}: ${opens} - ${closes}`);
    } else {
      baseConsecutive.push(index);
    }
  });

  const consecutive = getConsecutive(baseConsecutive) as number[][];
  consecutive.forEach((values) => {
    const first = values[0];
    const last = values[values.length - 1];

    const text =
      values.length <= 1
        ? `${WEEK_DAYS_SHORT[first]}: Closed`
        : `${WEEK_DAYS_SHORT[first]}-${WEEK_DAYS_SHORT[last]}: Closed`;

    items.splice(first, 0, text);
  });

  return items;
};

interface EditLocationScheduleProps {
  initValue?: string[];
  onChange?: (schedule: string[]) => void;
}

const EditLocationSchedule = ({ initValue, onChange }: EditLocationScheduleProps) => {
  // Hydrated once from `initValue` via lazy state init rather than a mount effect: callers only
  // render this after their location fetch resolves, and initialising directly avoids the render
  // pass where the old default (24/7 checked) would emit a schedule the location never had.
  const [initialSchema] = useState(() => getValidSchema(initValue));
  const [everydayChecked, setEverydayChecked] = useState(initialSchema.everydayChecked);
  const [daysChecked, setDaysChecked] = useState(initialSchema.daysChecked);
  const [rangeValues, setRangeValues] = useState(initialSchema.rangeValues);
  // Stays true only while the location has no stored hours and the user hasn't picked any.
  const [hoursUnset, setHoursUnset] = useState(initialSchema.hoursUnset);

  useEffect(() => {
    if (everydayChecked) {
      setDaysChecked(DEFAULT_WEEK_VALUES);
      setRangeValues(DEFAULT_RANGE_VALUES);
    }
  }, [everydayChecked]);

  useEffect(() => {
    // Emitting here would turn "hours were never set" into an explicit schedule on save.
    if (hoursUnset) {
      return;
    }

    const schedule = getValidSchedule(daysChecked, everydayChecked, rangeValues);
    onChange?.(schedule);
  }, [daysChecked, everydayChecked, rangeValues, hoursUnset]);

  const validDateRanges = useMemo(() => {
    if (everydayChecked) {
      return [];
    }

    return WEEK_DAYS_LONG.map((item, index) => (daysChecked[index] ? item : null));
  }, [daysChecked, everydayChecked]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHoursUnset(false);
    setEverydayChecked(event.target.checked);
  };

  const handleChangeDays = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setHoursUnset(false);
    setDaysChecked((prevValue) => {
      const newValue = [...prevValue];
      newValue[index] = event.target.checked;

      return newValue;
    });
  };

  const handleChangeRanges = (index: number) => (newValues: any) => {
    setHoursUnset(false);
    setRangeValues((prevValue) => {
      const newValue = [...prevValue];
      newValue[index] = newValues;

      return newValue;
    });
  };

  return (
    <Box mt={2}>
      <Typography variant="h6">Operation Schedule</Typography>

      {hoursUnset && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          data-test="operation-schedule-unset"
        >
          No hours set for this location yet. Pick a schedule below - until then nothing is
          published to Google Business Profile.
        </Typography>
      )}

      <FormControlLabel
        labelPlacement="start"
        label="24/7 (Every day, Monday to Sunday)"
        control={
          <CustomCheckbox
            name="24/7"
            checked={everydayChecked}
            onChange={handleChange}
            data-test="operation-schedule-24"
          />
        }
        sx={{
          margin: 0,
          '& span': {
            fontSize: '12px !important',
          },
        }}
      />

      <Stack spacing={1} direction="row" mt={1}>
        {WEEK_DAYS_SHORT.map((day, index) => (
          <EditLocationScheduleChip
            key={`edit-Location-chip-${day}`}
            day={day}
            disabled={everydayChecked}
            checked={daysChecked[index]}
            onChange={handleChangeDays(index)}
            rangeValues={rangeValues}
            onChangeRange={setRangeValues}
          />
        ))}
      </Stack>

      <Stack spacing={3} direction="column" mt={1}>
        {validDateRanges.map((day, index) => {
          if (!day) {
            return null;
          }

          return (
            <EditLocationScheduleRange
              key={`edit-Location-range-${day}`}
              day={day}
              values={rangeValues[index]}
              onChange={handleChangeRanges(index)}
            />
          );
        })}
      </Stack>
    </Box>
  );
};

export default EditLocationSchedule;
